import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface GoalAlertEvent {
  id: string;
  fixture_id: number;
  player_name: string | null;
  minute: number;
  team_id: string | null;
  created_at: string;
}

/**
 * Subscribes to game_events INSERTs via a dedicated realtime channel.
 * Returns the currently-active goal (shown for 5s) and whether the ticker is locked.
 *
 * Key guarantees:
 * - Dedup by game_events.id (processedIdsRef)
 * - No replay on mount (mountedAtRef guards against old events)
 * - FIFO queue for consecutive goals
 */
export function useTickerGoalEvents() {
  const [activeGoal, setActiveGoal] = useState<GoalAlertEvent | null>(null);
  const [isGoalLock, setIsGoalLock] = useState(false);

  const queueRef = useRef<GoalAlertEvent[]>([]);
  const processedIdsRef = useRef<Set<string>>(new Set());
  const mountedAtRef = useRef<string>(new Date().toISOString());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const processNext = useCallback(() => {
    if (queueRef.current.length === 0) {
      setActiveGoal(null);
      setIsGoalLock(false);
      return;
    }
    const next = queueRef.current.shift()!;
    setActiveGoal(next);
    setIsGoalLock(true);

    timerRef.current = setTimeout(() => {
      processNext();
    }, 10000);
  }, []);

  const lockRef = useRef(false);
  useEffect(() => { lockRef.current = isGoalLock; }, [isGoalLock]);

  const enqueueGoal = useCallback(
    async (event: GoalAlertEvent) => {
      // Dedup
      if (processedIdsRef.current.has(event.id)) return;
      processedIdsRef.current.add(event.id);

      // Mount guard — ignore events created before this session
      if (event.created_at < mountedAtRef.current) return;

      // Live guard — only announce goals from games currently live.
      // Background backfills of old/finished games must never trigger the alert.
      const { data: game } = await supabase
        .from("games")
        .select("status")
        .eq("fixture_id", event.fixture_id)
        .maybeSingle();
      if (game?.status !== "live") return;

      if (lockRef.current || queueRef.current.length > 0) {
        queueRef.current.push(event);
      } else {
        lockRef.current = true;
        setActiveGoal(event);
        setIsGoalLock(true);
        timerRef.current = setTimeout(() => {
          processNext();
        }, 10000);
      }
    },
    [processNext]
  );

  useEffect(() => {
    mountedAtRef.current = new Date().toISOString();

    const channel = supabase
      .channel("ticker-goal-alerts")
      .on(
        "postgres_changes" as any,
        {
          event: "INSERT",
          schema: "public",
          table: "game_events",
        },
        (payload: any) => {
          const row = payload.new;
          if (!row) return;

          // Only confirmed goals (not missed penalties)
          if (row.event_type !== "Goal") return;
          if (row.event_detail === "Missed Penalty") return;

          enqueueGoal({
            id: row.id,
            fixture_id: row.fixture_id,
            player_name: row.player_name ?? null,
            minute: row.time_elapsed ?? 0,
            team_id: row.team_id ?? null,
            created_at: row.created_at ?? new Date().toISOString(),
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { activeGoal, isGoalLock };
}
